/**
 * Route a ground move (taxi / tow) so it merges onto the shared taxiway spine
 * instead of cutting straight across the field.
 *
 * Both endpoints project onto the spine (foot of perpendicular, clamped to the
 * segment); the route is `from → connector(from) → connector(to) → to`. Because
 * every aircraft's connectors land on the same spine line, their routes overlap
 * along the spine — the "drain onto the taxiway" look — instead of fanning out.
 * Corners get rounded by `smoothPath` at the call site.
 */
import type { FieldGraph } from '../data/fieldGraph'
import type { Pt } from './easing'

/** Foot of the perpendicular from p onto segment [a,b], clamped to the segment. */
function projectOntoSegment(p: Pt, a: Pt, b: Pt): Pt {
  const abx = b[0] - a[0]
  const abz = b[1] - a[1]
  const len2 = abx * abx + abz * abz
  if (len2 < 1e-9) return [a[0], a[1]]
  let t = ((p[0] - a[0]) * abx + (p[1] - a[1]) * abz) / len2
  t = Math.max(0, Math.min(1, t))
  return [a[0] + abx * t, a[1] + abz * t]
}

/** Append `p` only if it is meaningfully far from the previous point (avoids degenerate segments). */
function pushIfFar(out: Pt[], p: Pt, minFt = 6): void {
  const last = out[out.length - 1]
  if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > minFt) out.push([p[0], p[1]])
}

/** Build a spine-merged waypoint list from `from` to `to` (scene feet [x, z]). */
export function routeTaxi(graph: FieldGraph, from: Pt, to: Pt): Pt[] {
  const spine = graph.spine
  if (!spine) return [[from[0], from[1]], [to[0], to[1]]]
  const [a, b] = spine
  const cFrom = projectOntoSegment(from, a, b)
  const cTo = projectOntoSegment(to, a, b)

  const out: Pt[] = [[from[0], from[1]]]
  pushIfFar(out, cFrom)
  pushIfFar(out, cTo)
  pushIfFar(out, [to[0], to[1]])
  return out
}
